<template>
  <div class="learning-platform-page">
    <!-- 1. Top Banner Section -->
    <div class="top-banner">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('software.cloudPlatform.bannerTitle') }}</h1>
        <p class="banner-desc">
          {{ $t('software.cloudPlatform.bannerDesc') }}
        </p>
        <div class="banner-img-container">
          <img :src="bannerImage"
               :alt="$t('software.cloudPlatform.bannerImageAlt')"
               class="banner-main-img" />
          <!-- Optional floating elements based on UI screenshot if needed, sticking to basics first -->
        </div>
      </div>
    </div>

    <!-- 2. Explore Section -->
    <div class="explore-section">
      <div class="section-container">
        <h2 class="section-title">{{ $t('software.cloudPlatform.exploreTitle') }}</h2>

        <!-- Tabs -->
        <div class="explore-tabs">
          <div class="tab-item"
               v-for="(tab, index) in exploreTabs"
               :key="index"
               @click="currentTab = index"
               :class="{ active: currentTab === index }">
            <div class="tab-icon-wrapper">
              <img :src="tab.icon"
                   :alt="tab.name"
                   class="tab-icon" />
            </div>
            <span class="tab-name">{{ tab.name }}</span>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="tab-content-wrapper">
          <transition name="fade"
                      mode="out-in">
            <img :key="currentTab"
                 :src="exploreTabs[currentTab].contentImage"
                 :alt="$t('software.cloudPlatform.tabContentAlt')"
                 class="tab-content-img" />
          </transition>
        </div>
      </div>
    </div>

    <!-- 3. Learning Platform Intro Section -->
    <div class="platform-intro-section">

      <div class="section-container">
        <h2 class="section-title">{{ $t('software.cloudPlatform.introTitle') }}</h2>

        <div class="platform-intro-content">
          <div class="intro-image-side">
            <img :src="introImage"
                 :alt="$t('software.cloudPlatform.introImageAlt')"
                 class="intro-side-img" />
          </div>
          <div class="intro-text-side">
            <div class="intro-item"
                 v-for="(item, index) in introItems"
                 :key="index">
              <h3 class="intro-item-title">{{ item.title }}</h3>
              <p class="intro-item-desc">{{ item.desc }}</p>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="gallery-banner-section"
         :style="{ backgroundImage: `url(${galleryBannerImage})` }">
      <div class="section-container">
        <div class="gallery-text">
          <h2 class="gallery-title">{{ $t('software.cloudPlatform.galleryTitle') }}</h2>
          <p class="gallery-desc">{{ $t('software.cloudPlatform.galleryDesc') }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'CloudPlatform',
  data() {
    return {
      currentTab: 0,
      bannerImage: require('@/assets/ruanjian/cloudbanner.png'),
      introImage:
        require('@/assets/ruanjian/cloudedu.png'),
      galleryBannerImage: require('@/assets/ruanjian/cloudbot.png'),
      exploreTabAssets: [
        {
          icon: require('@/assets/ruanjian/cloudicon1.png'),
          contentImage:
            require('@/assets/ruanjian/cloudicon-1.png')
        },
        {
          icon: require('@/assets/ruanjian/cloudicon2.png'),
          contentImage:
            require('@/assets/ruanjian/cloudicon-2.png')
        },
        {
          icon: require('@/assets/ruanjian/cloudicon3.png'),
          contentImage:
            require('@/assets/ruanjian/cloudicon-3.png')
        }
      ],
    }
  },
  computed: {
    cloudPlatformCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return (messages.software && messages.software.cloudPlatform) || {}
    },
    exploreTabs() {
      const tabs = this.cloudPlatformCopy.exploreTabs || []
      return tabs.map((item, index) => {
        const asset = this.exploreTabAssets[index] || {}
        return {
          name: item.name || '',
          icon: asset.icon || '',
          contentImage: asset.contentImage || ''
        }
      })
    },
    introItems() {
      return this.cloudPlatformCopy.introItems || []
    },
  }
}
</script>

<style scoped>
.learning-platform-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  overflow-x: hidden;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
}

/* 1. Top Banner Section */
.top-banner {
  width: 100%;
  height: 880px;
  background-color: #2ea2fa; /* Fallback */
  background: linear-gradient(135deg, #2ea2fa 0%, #1a82d6 100%);
  position: relative;
  text-align: center;
  padding-top: 80px; /* Adjust padding */
}

.banner-content {
  width: 1200px;
  margin: 0 auto;
  position: relative;
  height: 100%;
}

.banner-title {
  font-size: 48px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 30px;
}

.banner-desc {
  font-size: 24px;
  color: #fff;
  line-height: 1.5;
  margin-bottom: 60px;
}

.banner-img-container {
  width: 1200px;
  height: 686px;
  position: absolute;
  left: 0;
  bottom: -84px; /* Overflow by 134px */
  z-index: 10;
  border-radius: 20px;
}

.banner-main-img {
  width: 105%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
}

/* 2. Explore Section */
.explore-section {
  width: 100%;
  height: 1078px;
  background-color: #f5fbff;
  padding-top: 200px; /* 134px gap + padding for spacing */
  /* User said "distance from top is 134px". Since the image overflows 134px, 
     we need to push the content down. 
     The gap is 134px visual space BELOW the image? Or from the banner bottom?
     If image overflows banner by 134px, it sits ON TOP of explore section.
     So Explore section top padding should be at least 134px + some spacing.
     Let's use padding-top: 200px to give room for the floating image and title space.
  */
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
}

.explore-tabs {
  width: 1200px;
  height: 209px;
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 50px;
}

.tab-item {
  width: 210px;
  height: 209px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 32px;
  transition: all 0.3s ease;
}

.tab-item:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.tab-item.active {
  background-color: #fff;
}

.tab-icon-wrapper {
  width: 74px;
  height: 74px;
  margin-bottom: 20px;
}

.tab-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.tab-name {
  font-size: 20px;
  color: #333;
}

.tab-content-wrapper {
  width: 922px;
  height: 535px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
}

.tab-content-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 3. Platform Intro Section */
.platform-intro-section {
  width: 100%;
  height: 843px;
  background-color: #fff;
  padding-top: 80px;
}

.platform-intro-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 60px;
}

.intro-text-side {
  width: 500px; /* Adjust to fit layout */
}

.intro-item {
  margin-bottom: 50px;
}

.intro-item-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}

.intro-item-desc {
  font-size: 20px;
  font-weight: normal; /* "不加粗" */
  color: #333;
  line-height: 1.6;
}

.intro-image-side {
  width: 600px;
  height: 509px;
}

.intro-side-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
}

/* 4. Gallery Banner Section */
.gallery-banner-section {
  width: 100%;
  height: 573px;
  background-color: #f5f5f5;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

.gallery-text {
  height: 573px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.gallery-title {
  font-size: 34px;
  color: #333;
  font-weight: normal;
  margin: 0 0 18px;
}

.gallery-desc {
  font-size: 34px;
  color: #333;
  font-weight: normal;
  margin: 0;
}

/* 5. Download Section */
.download-section {
  width: 100%;
  height: 493px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.download-list {
  display: flex;
  justify-content: center;
  gap: 100px;
  margin-top: 60px;
}

.download-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.app-icon-wrapper {
  width: 80px; /* Estimated */
  height: 80px;
  margin-bottom: 20px;
}

.app-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.app-name {
  font-size: 16px;
  color: #333;
  margin-bottom: 25px;
}

.download-btn {
  width: 126px;
  height: 41px;
  background-color: #2ea2fa;
  border-radius: 21px;
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.download-btn:hover {
  background-color: #258cd6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
